import BillingModel from "../../../types/billing-model";
import BillingFormData, {
  BillingFormDataContributions,
  BillingSummaryData,
} from "../types/billing-form.data";
import BillingsNode from "../../../common/types/billings-node";
import googleMapsRouteService from "../../../../../common/utils/google-maps-route/google-maps-route.service";
import BillingsCargoTaxiContributionsDiscountTableRow from "../../common/contributions/discount/types/billings-cargo-taxi-contributions-discount-table-row";
import billingsCargoTaxiHelper from "../billings-cargo-taxi.helper";
import TabsData from "../../../../../common/components/tabs/common/types/tabs-data";
import billingsTranslationsHelper from "../../../../../languages/billings-translations.helper";
import BillingsCargoTaxiContributionsDiscountTableComponent from "../../common/contributions/discount/table/billings-cargo-taxi-contributions-discount-table.component";
import SingleSelectComponent from "../../../../../common/components/form/select/single-select/single-select.component";
import NumericInputComponent from "../../../../../common/components/form/input/numeric-input/numeric-input.component";
import InputComponent from "../../../../../common/components/form/input/input.component";
import TableButtonComponent from "../../../../../common/components/table/button/table-button.component";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import BillingsCargoTaxiContribution, {
  BillingsCargoTaxiContributionDiscountType,
} from "../../common/contributions/discount/types/billings-cargo-taxi-contributions-discount";
import BillingContributionValidationResult from "../types/billing-contributions-validation-result";
import BillingsCargoTaxiContributions from "../../common/contributions/discount/types/billings-cargo-taxi-contributions-discount";
import FormErrorComponent from "../../../../../common/components/form/error/form-error.component";
import BillingsCargoTaxiDetails, {
  BillingsCargoTaxiDetailsBillingContribution,
  BillingsCargoTaxiDetailsBillingContributionContractCorrectionPercentage,
  BillingsCargoTaxiDetailsBillingContributionDiscountBeingLate,
  BillingsCargoTaxiDetailsBillingContributionDiscountIncompatibleCar,
  BillingsCargoTaxiDetailsBillingContributionDiscountOptimization,
  BillingsCargoTaxiDetailsBillingContributionDiscountOther,
  BillingsCargoTaxiDetailsBillingContributionType,
  BillingsCargoTaxiDetailsBillingNode,
  BillingsCargoTaxiDetailsContract,
} from "../../../../../common/services/billings/cargo-taxi/details/billings-cargo-taxi-details";
import BillingStatus from "../../../types/billing-status";
import ContractDetails from "../types/contract-details";
import BillingsOrderCargoTaxiRecalculate, {
  BillingsOrderCargoTaxiRecalculateBillingNode,
  BillingsOrderCargoTaxiRecalculateBillingContributionType,
  BillingsOrderCargoTaxiRecalculateBillingContributionContractCorrectionPercentage,
  BillingsOrderCargoTaxiRecalculateBillingContributionDiscountOptimization,
  BillingsOrderCargoTaxiRecalculateBillingContributionDiscountBeingLate,
  BillingsOrderCargoTaxiRecalculateBillingContributionDiscountIncompatibleCar,
  BillingsOrderCargoTaxiRecalculateBillingContributionDiscountOther,
  BillingsOrderCargoTaxiRecalculateBillingContribution,
  BillingsOrderCargoTaxiRecalculateContract,
} from "../../../../../common/services/billings/order/cargo-taxi/recalculate/billings-order-cargo-taxi-recalculate";

const createBillingNode = (
  node:
    | BillingsCargoTaxiDetailsBillingNode
    | BillingsOrderCargoTaxiRecalculateBillingNode,
  index: number,
  nodes:
    | BillingsCargoTaxiDetailsBillingNode[]
    | BillingsOrderCargoTaxiRecalculateBillingNode[],
): BillingsNode => {
  const previousItem = index - 1 >= 0 ? nodes[index - 1] : null;

  const url =
    previousItem !== null
      ? googleMapsRouteService.createGoogleMapsRouteUrl([
          { latitude: previousItem.lat, longitude: previousItem.lon },
          { latitude: node.lat, longitude: node.lon },
        ])
      : "";

  const result: BillingsNode = {
    id: node.uuid,
    nodeId: node.nodeUuid,
    lat: node.lat,
    lon: node.lon,
    description: node.description,
    checkoutDate: node.checkoutDate ?? null,
    checkoutEventUuid: node.checkoutEventUuid ?? null,
    plannedDate: node.plannedDate ?? null,
    plannedDistance: node.plannedDistance,
    orderId: node.orderUuid ?? null,
    distance: node.distance,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    isHighwayAllowed: node.allowCharge,
    isEditable: node.editableCoordinates,
    googleMapsUrl: url,
    position: index + 1,
  };

  return result;
};

const createBillingNodes = (
  nodes:
    | BillingsCargoTaxiDetailsBillingNode[]
    | BillingsOrderCargoTaxiRecalculateBillingNode[],
): BillingsNode[] => {
  return nodes.map(createBillingNode);
};

const createBillingDiscountContributionType = (
  contributionType:
    | BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_BEING_LATE
    | BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR
    | BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OPTIMIZATION
    | BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OTHER
    | BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_BEING_LATE
    | BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR
    | BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_OPTIMIZATION
    | BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_OTHER,
):
  | BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE
  | BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR
  | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION
  | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER => {
  switch (contributionType) {
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_BEING_LATE:
    case BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_BEING_LATE:
      return BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE;
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR:
    case BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR:
      return BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR;
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OPTIMIZATION:
    case BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_OPTIMIZATION:
      return BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION;
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OTHER:
    case BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_OTHER:
      return BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER;
  }
};

const createBillingDiscountContribution = (
  contribution:
    | BillingsCargoTaxiDetailsBillingContributionContractCorrectionPercentage
    | BillingsCargoTaxiDetailsBillingContributionDiscountOptimization
    | BillingsCargoTaxiDetailsBillingContributionDiscountBeingLate
    | BillingsCargoTaxiDetailsBillingContributionDiscountIncompatibleCar
    | BillingsCargoTaxiDetailsBillingContributionDiscountOther
    | BillingsOrderCargoTaxiRecalculateBillingContributionContractCorrectionPercentage
    | BillingsOrderCargoTaxiRecalculateBillingContributionDiscountOptimization
    | BillingsOrderCargoTaxiRecalculateBillingContributionDiscountBeingLate
    | BillingsOrderCargoTaxiRecalculateBillingContributionDiscountIncompatibleCar
    | BillingsOrderCargoTaxiRecalculateBillingContributionDiscountOther,
): BillingsCargoTaxiContributions => {
  switch (contribution.type) {
    case BillingsCargoTaxiDetailsBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE:
    case BillingsOrderCargoTaxiRecalculateBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE:
      return {
        type: BillingsCargoTaxiContributionDiscountType.CONTRACT_CORRECTION_PERCENTAGE,
        percentage: contribution.percentage,
        amount: contribution.amount,
      };
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_BEING_LATE:
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR:
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OPTIMIZATION:
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OTHER:
    case BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_BEING_LATE:
    case BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR:
    case BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_OPTIMIZATION:
    case BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_OTHER:
      return {
        type: createBillingDiscountContributionType(contribution.type),
        comment: contribution.comment ?? "",
        amount: contribution.amount,
      };
  }
};

const createBillingContributions = (
  contributions:
    | BillingsCargoTaxiDetailsBillingContribution[]
    | BillingsOrderCargoTaxiRecalculateBillingContribution[],
): BillingFormDataContributions => {
  const discountContributions = contributions.filter(
    (contribution) =>
      contribution &&
      [
        BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_BEING_LATE,
        BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR,
        BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OPTIMIZATION,
        BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OTHER,
        BillingsCargoTaxiDetailsBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE,
        BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_BEING_LATE,
        BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR,
        BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_OPTIMIZATION,
        BillingsOrderCargoTaxiRecalculateBillingContributionType.DISCOUNT_OTHER,
        BillingsOrderCargoTaxiRecalculateBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE,
      ].includes(contribution.type),
  ) as (
    | BillingsCargoTaxiDetailsBillingContributionContractCorrectionPercentage
    | BillingsCargoTaxiDetailsBillingContributionDiscountOptimization
    | BillingsCargoTaxiDetailsBillingContributionDiscountBeingLate
    | BillingsCargoTaxiDetailsBillingContributionDiscountIncompatibleCar
    | BillingsCargoTaxiDetailsBillingContributionDiscountOther
    | BillingsOrderCargoTaxiRecalculateBillingContributionContractCorrectionPercentage
    | BillingsOrderCargoTaxiRecalculateBillingContributionDiscountOptimization
    | BillingsOrderCargoTaxiRecalculateBillingContributionDiscountBeingLate
    | BillingsOrderCargoTaxiRecalculateBillingContributionDiscountIncompatibleCar
    | BillingsOrderCargoTaxiRecalculateBillingContributionDiscountOther
  )[];

  return {
    discount: discountContributions.map((contribution) =>
      createBillingDiscountContribution(contribution),
    ),
  };
};

const createBillingData = (
  data: BillingsCargoTaxiDetails | BillingsOrderCargoTaxiRecalculate,
) => {
  const billingDraft: BillingFormData = {
    billingNodes: createBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
  };

  return billingDraft;
};

const createBillingSummaryContract = (
  contract:
    | BillingsCargoTaxiDetailsContract
    | BillingsOrderCargoTaxiRecalculateContract,
): ContractDetails => {
  return {
    companyName: contract.cargoCompany.displayName,
    discountLimit: contract.discountLimit,
    haltingTimeRate: contract.haltingTimeRate,
    distanceRate: contract.distanceRate,
    isHighwayAllowedDuringRide: contract.allowChargeDuringRide,
    isHighwayAllowedWhenApproaching: contract.allowChargeWhileApproaching,
    isHighwayAllowedWhileReturning: contract.allowChargeWhileReturning,
    model: contract.model,
    haltingTimeAppliedAfter: contract.haltingTimeAfterMinutes,
  };
};

const createBillingSummaryData = (
  data: BillingsCargoTaxiDetails | BillingsOrderCargoTaxiRecalculate,
) => {
  const billingDraft: BillingSummaryData = {
    contractDetails: createBillingSummaryContract(data.contract),
    baseAmountValue: data.baseAmountValue,
    sumOfDiscounts: data.sumOfDiscounts,
    amountForChargeHaltings: data.amountForChargeHaltings,
    amountForChargeHighways: data.amountForChargeHighways,
    distance: data.distance,
    amountForDistance: data.amountForDistance,
    internalOrderId: data.cargoOrder.humanId,
    allContributionsAmount: data.allContributionsAmount,
    planEntryId: data.planEntryUuid,
    cargoOrderId: data.cargoOrder.uuid,
    date: data.date,
    status: data.status,
    passengers: data.passengers,
    contractCorrectionPercentage: data.contractCorrectionPercentage,
  };

  return billingDraft;
};

const createContributionsDiscountTableRow = (
  contribution: BillingsCargoTaxiContribution,
  contributions: BillingsCargoTaxiContribution[],
  position: number,
  deleteContribution: (position: number) => void,
  contributionsValidationResults: BillingContributionValidationResult,
  onContributionTypeChange: (
    position: number,
    type: BillingsCargoTaxiContributionDiscountType,
  ) => void,
  onContributionAmountChange: (position: number, amount: number | null) => void,
  onContributionCommentChange: (position: number, comment: string) => void,
  onContributionPercentageChange: (
    position: number,
    percentage: number | null,
  ) => void,
  onContributionTypeBlur: (position: number) => void,
  onContributionPercentageBlur: (position: number) => void,
  onContributionAmountBlur: (position: number) => void,
  onContributionCommentBlur: (position: number) => void,
): BillingsCargoTaxiContributionsDiscountTableRow => {
  const translations =
    billingsTranslationsHelper.getCargoTaxiContributionsTranslations();

  const typeSelectOptions =
    billingsCargoTaxiHelper.getContributionDiscountTypeSelectOptions();

  const typeSelectedOption =
    typeSelectOptions.find((option) => option.value === contribution.type) ??
    null;

  const validationResult = contributionsValidationResults.discount.find(
    (validation) => validation.position === position,
  );

  switch (contribution.type) {
    case BillingsCargoTaxiContributionDiscountType.CONTRACT_CORRECTION_PERCENTAGE:
      return {
        id: `${contribution.type}-${position}`,
        value: {
          type: (
            <div className="billings_contributions_row">
              <SingleSelectComponent
                value={typeSelectedOption}
                onChange={(value) =>
                  onContributionTypeChange(position, value?.value)
                }
                onBlur={() => onContributionTypeBlur(position)}
                options={typeSelectOptions}
                placeholder={translations.discount.form.typeSelectPlaceholder}
                idForTesting={`billings=cargo-taxi-edit-contribution-discount-type-select-${position}`}
                filterFunction={(option) =>
                  !contributions.find(
                    (contribution) => option.value == contribution.type,
                  )
                }
                autoFocus
                hasError={!!validationResult?.type.errorMessage}
              />
              <FormErrorComponent
                message={validationResult?.type.errorMessage}
              />
            </div>
          ),
          amount: (
            <div
              className="billings_contributions_not_required_row"
              title={String(contribution.amount)}
            >
              {contribution.amount}
            </div>
          ),
          comment: (
            <div
              className="billings_contributions_not_required_row"
              title={translations.attributeNotApplicable}
            >
              {translations.attributeNotApplicable}
            </div>
          ),
          percentage: (
            <div
              className="billings_contributions_row"
              title={String(contribution.percentage)}
            >
              <NumericInputComponent
                value={contribution.percentage}
                onChange={(value) => {
                  onContributionPercentageChange(position, value);
                }}
                onBlur={() => {
                  onContributionPercentageBlur(position);
                }}
                placeholder={translations.discount.form.percentagePlaceholder}
                idForTesting={`billings-taxi-taxi-edit-contribution-discount-percentage-${position}`}
                decimalPrecision={2}
                hasError={!!validationResult?.percentage.errorMessage}
              />
              <FormErrorComponent
                message={validationResult?.percentage.errorMessage}
              />
            </div>
          ),
          actions: (
            <div className="billings_contributions_delete_button">
              <TableButtonComponent
                icon={faTrash}
                onClick={() => {
                  deleteContribution(position);
                }}
                type="danger"
                title={
                  translations.discount.table.actions
                    .deleteContributionButtonTitle
                }
              />
            </div>
          ),
        },
      };
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER:
      return {
        id: `${contribution.type}-${position}`,
        value: {
          type: (
            <div className="billings_contributions_row">
              <SingleSelectComponent
                value={typeSelectedOption}
                onChange={(value) =>
                  onContributionTypeChange(position, value?.value)
                }
                onBlur={() => onContributionTypeBlur(position)}
                options={typeSelectOptions}
                placeholder={translations.discount.form.typeSelectPlaceholder}
                idForTesting={`billings-cargo-taxi-edit-contribution-discount-type-select-${position}`}
                filterFunction={(option) =>
                  !contributions.find(
                    (contribution) => option.value == contribution.type,
                  )
                }
                autoFocus
                hasError={!!validationResult?.type.errorMessage}
              />
              <FormErrorComponent
                message={validationResult?.type.errorMessage}
              />
            </div>
          ),
          amount: (
            <div
              className="billings_contributions_row"
              title={String(contribution.amount)}
            >
              <NumericInputComponent
                value={contribution.amount}
                onChange={(value) =>
                  onContributionAmountChange(position, value)
                }
                onBlur={() => onContributionAmountBlur(position)}
                placeholder={translations.discount.form.amountPlaceholder}
                idForTesting={`billings-cargo-taxi-edit-contribution-discount-amount-${position}`}
                decimalPrecision={2}
                hasError={!!validationResult?.amount.errorMessage}
              />
              <FormErrorComponent
                message={validationResult?.amount.errorMessage}
              />
            </div>
          ),
          comment: (
            <div
              className="billings_contributions_row"
              title={contribution.comment}
            >
              <InputComponent
                value={contribution.comment}
                onChange={(value) =>
                  onContributionCommentChange(position, value)
                }
                onBlur={() => onContributionCommentBlur(position)}
                placeholder={translations.discount.form.commentPlaceholder}
                idForTesting={`billings-cargo-taxi-edit-contribution-discount-comment-${position}`}
                hasError={!!validationResult?.comment.errorMessage}
              />
              <FormErrorComponent
                message={validationResult?.comment.errorMessage}
              />
            </div>
          ),
          percentage: (
            <div
              className="billings_contributions_not_required_row"
              title={translations.attributeNotApplicable}
            >
              {translations.attributeNotApplicable}
            </div>
          ),
          actions: (
            <div className="billings_contributions_delete_button">
              <TableButtonComponent
                icon={faTrash}
                onClick={() => {
                  deleteContribution(position);
                }}
                type="danger"
                title={
                  translations.discount.table.actions
                    .deleteContributionButtonTitle
                }
              />
            </div>
          ),
        },
      };
    default:
      return {
        id: String(position),
        value: {
          type: (
            <div>
              <SingleSelectComponent
                value={typeSelectedOption}
                onChange={(value) =>
                  onContributionTypeChange(position, value?.value)
                }
                onBlur={() => onContributionTypeBlur(position)}
                options={typeSelectOptions}
                placeholder={translations.discount.form.typeSelectPlaceholder}
                idForTesting={`billings-cargo-taxi-edit-contribution-discount-type-select-${position}`}
                filterFunction={(option) =>
                  !contributions.find(
                    (contribution) => option.value == contribution.type,
                  )
                }
                autoFocus
                hasError={!!validationResult?.type.errorMessage}
              />
              <FormErrorComponent
                message={validationResult?.type.errorMessage}
              />
            </div>
          ),
          amount: <div></div>,
          comment: <div></div>,
          percentage: <div></div>,
          actions: <div className="d-flex"></div>,
        },
      };
  }
};

const createTabsData = (
  contributions: BillingFormDataContributions,
  isDataLoading: boolean,
  deleteContribution: (position: number) => void,
  contributionsValidationResults: BillingContributionValidationResult,
  onContributionTypeChange: (
    position: number,
    type: BillingsCargoTaxiContributionDiscountType,
  ) => void,
  onContributionAmountChange: (position: number, amount: number | null) => void,
  onContributionCommentChange: (position: number, comment: string) => void,
  onContributionPercentageChange: (
    position: number,
    percentage: number | null,
  ) => void,
  onContributionTypeBlur: (position: number) => void,
  onContributionPercentageBlur: (position: number) => void,
  onContributionAmountBlur: (position: number) => void,
  onContributionCommentBlur: (position: number) => void,
): TabsData => {
  const translations =
    billingsTranslationsHelper.getCargoTaxiContributionsTranslations();

  const discountTableRows: BillingsCargoTaxiContributionsDiscountTableRow[] =
    contributions.discount.map((contribution, index) =>
      createContributionsDiscountTableRow(
        contribution,
        contributions.discount,
        index,
        deleteContribution,
        contributionsValidationResults,
        onContributionTypeChange,
        onContributionAmountChange,
        onContributionCommentChange,
        onContributionPercentageChange,
        onContributionTypeBlur,
        onContributionPercentageBlur,
        onContributionAmountBlur,
        onContributionCommentBlur,
      ),
    );

  const DiscountTabContent = (
    <BillingsCargoTaxiContributionsDiscountTableComponent
      isLoading={isDataLoading}
      rows={discountTableRows}
    />
  );

  const tabsData: TabsData = [
    {
      label: translations.discount.title,
      content: DiscountTabContent,
      counter: contributions.discount.filter(
        (contribution) => contribution.type,
      ).length,
      totalTableRows: discountTableRows.length,
    },
  ];

  return tabsData;
};

const billingDataFactory = {
  createBillingData,
  createBillingSummaryData,
  createTabsData,
};

export default billingDataFactory;
